import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { FormHeader, FormRow, InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Switch } from '@creditornot/cb-switch';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { importMenu } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { APIErrorNotification, WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { trackImportJSONMenu, trackSelectJSONForImport } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { PartialExportedMenu, readAndParseFile, removeInvalidLocalisedValues } from './utils';
import { reportMenuImport } from '../../behavioral';
import { DeprecationNote } from '../DeprecationNote';
import JsonFileDropzone from '../JsonFileDropzone';

type Props = {
  isOpen: boolean;
  menuId: string;
  onClose: () => void;
};

const Root = styled(WoltModal)`
  min-width: 320px;
  max-width: 640px;
  max-height: 95%;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  direction: ${({ theme }) => theme.cbDirection};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const Content = styled(ModalContent)`
  padding: 0;
  background: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const SmartImportDescriptionContainer = styled(T.Body3)`
  margin: 24px;
  display: flex;
  flex-direction: column;
`;

const FileContainer = styled.div`
  padding: 0 24px 24px;
  width: 100%;
`;

const StyledFormRow = styled(FormRow)`
  align-items: center;
`;

const SwitchContainer = styled(InputContainer)``;

const ExplanationContainer = styled.div`
  padding: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const AdvancedSettingHeader = styled(FormHeader)`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${typographyCss.Small()}
`;

const SuccessText = styled(T.Body3)`
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const FooterContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NetworkErrorContainer = styled(Alert).attrs({ variant: 'error' })``;

const ImportError = (e: any) => {
  if (!!e.error?.response) {
    return <APIErrorNotification error={e.error} />;
  }

  return (
    <NetworkErrorContainer>
      <FormattedMessage
        id="menu.importMenuNetworkError"
        values={{ button: chunks => <TextButton onClick={() => window.location.reload()}>{chunks}</TextButton> }}
      />
    </NetworkErrorContainer>
  );
};

const ImportMenuModal = ({ isOpen, menuId, onClose }: Props) => {
  const intl = useIntl();
  const { updateMenuData } = useMenuData();

  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [importData, setImportData] = useState<PartialExportedMenu | undefined>(undefined);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [keepPriceInfo, setKeepPriceInfo] = useState(false);
  const [keepAvailabilityInfo, setKeepAvailabilityInfo] = useState(false);

  const { isSuccess, isLoading, isError, error, mutate } = useMutation(
    () => {
      if (!importData) {
        return Promise.reject();
      }
      const sanitizedData = removeInvalidLocalisedValues(importData);
      return importMenu(menuId, sanitizedData, keepPriceInfo, keepAvailabilityInfo);
    },
    {
      onMutate: () => {
        reportMenuImport(keepPriceInfo, keepAvailabilityInfo);
      },
      onSuccess: updatedMenuContainer => {
        trackImportJSONMenu('Success', keepAvailabilityInfo, keepPriceInfo);
        updateMenuData({ actionType: 'updateMenuContainer', payload: updatedMenuContainer });
      },
      onError: error => {
        trackImportJSONMenu(`Failure: ${error}`, keepAvailabilityInfo, keepPriceInfo);
      },
    },
  );
  const handleFileChange = async (files: File[]) => {
    if (files.length > 0) {
      await readAndParseFile(files[0], intl)
        .then(menuImportData => setImportData(menuImportData))
        .then(() => trackSelectJSONForImport('Success'))
        .catch(error => {
          setFileError(error);
          trackSelectJSONForImport(`Failure: ${error}`);
        });
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Root isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="menu.smart-json-menu-import.header" />
      </ModalHeader>
      <DeprecationNote featureToBeDeprecated="JSON import/export" />
      <Content>
        {!isSuccess && !isError && (
          <>
            <SmartImportDescriptionContainer>
              <FormattedMessage id="menu.smart-json-menu-import.how-it-works" />
              <li>
                <FormattedMessage id="menu.smart-json-menu-import.step-one" />
              </li>
              <li>
                <FormattedMessage id="menu.smart-json-menu-import.step-two" />
              </li>
              <li>
                <FormattedMessage id="menu.smart-json-menu-import.step-three" />
              </li>
            </SmartImportDescriptionContainer>
            <FileContainer>
              <JsonFileDropzone
                fileName={fileName}
                onDrop={files => {
                  setFileError(undefined);
                  setFileName(files[0].name);
                  if (!files[0].name.endsWith('.json')) {
                    setFileError(intl.formatMessage({ id: 'menu.fileMustBeJson' }));
                    trackSelectJSONForImport('Failure: wrong file extension');
                  } else {
                    handleFileChange(files);
                  }
                }}
                hasError={!!fileError}
                loading={isLoading}
              />
              {!!fileError && (
                <InputAlert style={{ marginBlockStart: 8 }} variant="error">
                  {fileError}
                </InputAlert>
              )}
            </FileContainer>
            {showAdvancedSettings && (
              <div>
                <AdvancedSettingHeader horizontalPadding>
                  <FormattedMessage id="menu.importer-settings.price.header" />
                </AdvancedSettingHeader>
                <ExplanationContainer>
                  <T.Body3>
                    <FormattedMessage id="menu.importer-settings.price.hint" />
                  </T.Body3>
                </ExplanationContainer>
                <StyledFormRow
                  label={intl.formatMessage({ id: 'menu.importer-settings.price.label' })}
                  horizontalPadding
                  info={
                    <TooltipContainer>
                      <FormattedMessage id="menu.importer-settings.price.tooltip-header" />
                      <li>
                        <FormattedMessage id="menu-item.price" />
                      </li>
                      <li>
                        <FormattedMessage id="menu-item.discount" />
                      </li>
                      <li>
                        <FormattedMessage id="menu-option.choices-header" />:{' '}
                        <FormattedMessage id="menu-option.price-heading" />
                      </li>
                    </TooltipContainer>
                  }
                >
                  <SwitchContainer>
                    <Switch checked={keepPriceInfo} onChange={() => setKeepPriceInfo(!keepPriceInfo)} />
                  </SwitchContainer>
                </StyledFormRow>
                <AdvancedSettingHeader horizontalPadding>
                  <FormattedMessage id="menu.importer-settings.availability.header" />
                </AdvancedSettingHeader>
                <ExplanationContainer>
                  <T.Body3>
                    <FormattedMessage id="menu.importer-settings.availability.hint" />
                  </T.Body3>
                </ExplanationContainer>
                <StyledFormRow
                  label={intl.formatMessage({ id: 'menu.importer-settings.availability.label' })}
                  horizontalPadding
                  info={
                    <TooltipContainer>
                      <FormattedMessage id="menu.importer-settings.availability.tooltip-header" />{' '}
                      <li>
                        <FormattedMessage id="menu-item.enabled" />
                      </li>
                      <li>
                        <FormattedMessage id="special.availability" />
                      </li>
                      <li>
                        <FormattedMessage id="menu-item.time-available" />
                      </li>
                      <li>
                        <FormattedMessage id="menu-item.time-visible" />
                      </li>
                      <li>
                        <FormattedMessage id="inventory-management.type" />
                      </li>
                      <li>
                        <FormattedMessage id="menu-option.choices-header" />:{' '}
                        <FormattedMessage id="menu-item.enabled" />{' '}
                      </li>
                    </TooltipContainer>
                  }
                >
                  <SwitchContainer>
                    <Switch
                      checked={keepAvailabilityInfo}
                      onChange={() => setKeepAvailabilityInfo(!keepAvailabilityInfo)}
                    />
                  </SwitchContainer>
                </StyledFormRow>
              </div>
            )}
          </>
        )}
        {isSuccess && (
          <SuccessText>
            <FormattedMessage id="menu.importMenuSuccess" /> 👍
          </SuccessText>
        )}
        {isError && !!error && <ImportError error={error} />}
      </Content>
      <StyledModalFooter>
        {isSuccess && (
          <Button onClick={handleClose} variant="blue" size="small">
            <FormattedMessage id="state.close" />
          </Button>
        )}
        {!isSuccess && (
          <FooterContent>
            <Switch checked={showAdvancedSettings} onChange={() => setShowAdvancedSettings(oldValue => !oldValue)}>
              <T.Body3>
                <FormattedMessage id="menu.importer-settings.show-settings" />
              </T.Body3>
            </Switch>
            <div>
              <Button onClick={handleClose} variant="lightBlue" size="small" style={{ marginInlineEnd: '16px' }}>
                <FormattedMessage id="state.cancel" />
              </Button>
              <Button
                disabled={!importData || isLoading}
                variant="blue"
                size="small"
                onClick={() => {
                  mutate();
                }}
              >
                <FormattedMessage id="menu.importMenuHeader" />
              </Button>
            </div>
          </FooterContent>
        )}
      </StyledModalFooter>
    </Root>
  );
};

export default ImportMenuModal;
